import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import _ from "lodash";

import { graphql } from "lib/generated/app-service-gql";
import {
  CustomerDocumentsQuery,
  DeleteCustomerDocumentsMutation,
  DeleteCustomerDocumentsMutationVariables,
} from "lib/generated/app-service-gql/graphql";
import { useGraphQlMutation } from "../useGraphQlClient";
import { customerDocumentsKey } from "./useCustomerDocuments";

const DELETE_CUSTOMER_DOCUMENT_QUERY = graphql(`
  mutation deleteCustomerDocuments($request: DeleteCustomerDocumentRequest!) {
    deleteCustomerDocuments(DeleteCustomerDocumentRequest: $request) {
      id
      fileName
      resourceId
      documentType
      uiPath
      isFolder
    }
  }
`);

export function useDeleteCustomerDocuments(
  options?: UseMutationOptions<
    DeleteCustomerDocumentsMutation,
    unknown,
    DeleteCustomerDocumentsMutationVariables,
    unknown
  >,
) {
  const queryClient = useQueryClient();

  const { data, ...rest } = useGraphQlMutation(DELETE_CUSTOMER_DOCUMENT_QUERY, {
    ...options,
    onSuccess: async (data, variables, context) => {
      options?.onSuccess?.(data, variables, context);

      const grouped = _.groupBy(data.deleteCustomerDocuments, (doc) =>
        JSON.stringify({
          documentType: doc.documentType,
          resourceId: doc.resourceId,
          uiPath: doc.uiPath,
        }),
      );

      for (const [key, docs] of Object.entries(grouped)) {
        const { documentType, resourceId, uiPath } = JSON.parse(key);
        const idsToDelete = docs.map((doc) => doc.id);

        queryClient.setQueriesData(
          {
            queryKey: customerDocumentsKey({ documentType, resourceId, uiPath }),
            exact: false, // Allow matching without pagination
          },
          (prevData: CustomerDocumentsQuery | undefined) => {
            if (!prevData) return prevData;

            const previousItems = prevData.customerDocuments.items;
            const updatedItems = previousItems.filter((doc) => !idsToDelete.includes(doc.id));
            const removedCount = previousItems.length - updatedItems.length;

            return {
              customerDocuments: {
                items: updatedItems,
                totalCount: prevData.customerDocuments.totalCount - removedCount,
              },
            };
          },
        );
      }
    },
  });
  return { data: data, ...rest };
}
